import { useEffect, useState } from "react";
import { Progress } from "antd";
import { ICharacterFullImage } from "components/NewAvatarImage";
import LevelProgressBar from "components/LevelProgressBar";
import LevelTag from "components/LevelTag";
import WorkDetail from "components/WorkDetail";
import "./style.scss";

interface WorkContainerProps {
  currentUserRecord: any;
  scale?: number;
  ringWidth?: number;
  avatarWidth?: number;
  avatarContainerWidth?: number;
  avatarMaxWidth?: number;
  avatarHeight?: number;
  avatarTop?: number;
  avatarRight?: number;
  avatarTransformY?: number;
  adjustLegendPosition?: number;
  showUserName?: boolean;
}

const WorkContainer = ({
  currentUserRecord,
  scale = 1,
  ringWidth = 110,
  avatarWidth = 269,
  avatarContainerWidth = 269,
  avatarMaxWidth,
  avatarHeight,
  avatarTop = 50,
  avatarRight,
  avatarTransformY = -55,
  adjustLegendPosition = 1,
  showUserName = false,
}: WorkContainerProps) => {
  const [currentLineDetail, setCurrentLineDetail] = useState<any>(null);
  const [currentProcessDetail, setCurrentProcessDetail] = useState<any>(null);
  const [targetCycleTimeRange, setTargetCycleTimeRange] = useState<number>(0);
  const [targetCycleTimeType, setTargetCycleTimeType] = useState<number>(0);
  const [fullCharacterImage, setFullCharacterImage] = useState<ICharacterFullImage | null>(null);
  const [itemZIndex, setItemZIndex] = useState<{ hat: number, shoe: number, cloth: number }>({ hat: 2, shoe: 3, cloth: 4 })
  const [percent, setPercent] = useState<number>(0);

  useEffect(() => {
    if (currentUserRecord !== null) {
      setCurrentLineDetail(currentUserRecord.machining_line);
      setCurrentProcessDetail(currentUserRecord.machining_processes_detail);
      setTargetCycleTimeRange(currentUserRecord.target_time_range ?? 0);
      setTargetCycleTimeType(currentUserRecord.target_time_type ?? 0);

      const mainCharacter = currentUserRecord.user_characters?.find((item: any) => item.is_main === true);

      const bodyImage = mainCharacter.character.image_path;

      const hatItem = mainCharacter.user_character_items.find((item: any) => item?.user_items?.item?.item_type === "hat");
      const hatImage = hatItem?.user_items?.item?.asset?.image_path || undefined;

      const clothItem = mainCharacter.user_character_items.find((item: any) => item?.user_items?.item?.item_type === "cloth");
      const clothImage = clothItem?.user_items?.item?.asset?.image_path || undefined;

      const shoeItem = mainCharacter.user_character_items.find((item: any) => item?.user_items?.item?.item_type === "shoe");
      const shoeImage = shoeItem?.user_items?.item?.asset?.image_path || undefined;

      setFullCharacterImage({
        body: bodyImage,
        hat: hatImage,
        cloth: clothImage,
        shoe: shoeImage,
        characterId: mainCharacter?.character?.id || 0,
      });

      setItemZIndex({
        hat: hatItem?.user_items?.item?.z_index ?? 0,
        shoe: shoeItem?.user_items?.item?.z_index ?? 0,
        cloth: clothItem?.user_items?.item?.z_index ?? 0,
      });
    }
  }, [currentUserRecord]);

  useEffect(() => {
    if (!currentProcessDetail) {
      return;
    }
    if (currentProcessDetail && typeof currentProcessDetail.boosting_date === "number") {
      const boostingDate = currentProcessDetail.boosting_date;
      setPercent(20 * boostingDate);
    }
  }, [currentProcessDetail]);

  const containerClassName = scale !== 1 ? "work-container work-container--scaled" : "work-container";

  return (
    <div
      className={containerClassName}
      style={scale !== 1 ? { '--scale': scale } as React.CSSProperties : undefined}
    >
      {showUserName && (
        <div className="user-name-display">
          {currentUserRecord?.name || ''}
        </div>
      )}
      <div className="leftSection">
        <div className="processNameLevel">
          <div className="inner_wrapper mb-3">
            <p className={`process_Text ${(currentLineDetail?.line_jpn?.length || 0) > 7 ? 'process_Text--small' : ''}`}>
              {currentLineDetail?.line_jpn || ''}
            </p>
          </div>
          <div className="inner_wrapper mb-3">
            <p className={`process_Text ${(currentProcessDetail?.process_name?.length || 0) > 7 ? 'process_Text--small' : ''}`}>
              {currentProcessDetail?.process_name || ''}
            </p>
          </div>
          <div className="inner_wrapper mb-6">
            <p className={`process_Name ${(currentProcessDetail?.model_name?.length || 0) > 7 ? 'process_Name--small' : ''}`}>
              {currentProcessDetail?.model_name || ''}
            </p>
          </div>
        </div>
        <div className="levelBoostingWrapper">
          <div className="mb-12">
            <LevelProgressBar
              type="worker"
              level={(+currentUserRecord?.level || 1)}
              percentToBoostLevel={+currentUserRecord?.percent_to_up_level || 0}
            />
          </div>
          <div className="mb-16">
            <LevelProgressBar
              type="process"
              level={(currentProcessDetail?.level || 0) + 1}
              percentToBoostLevel={currentProcessDetail?.percent_to_boost_level}
            />
          </div>
          <div className="mb-3">
            <LevelTag type={"boost"} screen="working" />
          </div>
          <div className="boostingImage">
            <div className="boostingWrapper">
              <Progress
                strokeColor="#FA9158"
                trailColor="#C7C7C7"
                steps={10}
                percent={percent}
                type="line"
                showInfo={false}
                size={[19, 30]}
              />
            </div>
          </div>
        </div>
      </div>
      <WorkDetail
        currentProcessDetail={currentProcessDetail}
        targetCycleTimeRange={targetCycleTimeRange}
        targetCycleTimeType={targetCycleTimeType}
        fullCharacterImage={fullCharacterImage}
        itemZIndex={itemZIndex}
        width={980}
        height={730}
        ringWidth={ringWidth}
        avatarWidth={avatarWidth}
        avatarContainerWidth={avatarContainerWidth}
        avatarMaxWidth={avatarMaxWidth}
        avatarHeight={avatarHeight}
        avatarTop={avatarTop}
        avatarRight={avatarRight}
        avatarTransformY={avatarTransformY}
        adjustLegendPosition={adjustLegendPosition}
      />
    </div>
  );
};

export default WorkContainer;
